import {
	getUser,
	updateUser,
	updateUserImage,
	resetEmail,
	deleteAccount,
	activateAccount,
	deactivateAccount,
} from '../../../api';
import { onRequest } from '../..';
import { PropertyType, ActionsType, baseUrl } from '../../../common';

const removeDoublePartners = (partners: PropertyType[]) =>
	partners.filter((p) => !partners.some((pn) => pn?.options?.some((o: PropertyType) => o.value === p.value)));

const userStore = (user: PropertyType, notCurrentUser?: boolean, loggedUser?: PropertyType) => {
	const { id, username, email, permissions, image, isActive, lastActivity, activePartners } = user;
	const allPartners = loggedUser?.partners ?? [];

	return {
		userId: id,
		username,
		email,
		partners: notCurrentUser ? allPartners : removeDoublePartners(activePartners),
		permissions,
		image,
		isActive,
		lastActivity,
		activePartners,
	};
};

const userInit = async (payload: PropertyType) => {
	const { token, id } = payload;
	let uid: number | null = null;

	const stored = localStorage.getItem('user_data');
	if (stored) {
		const parsed = JSON.parse(stored);
		uid = parsed.uid;
	}

	const user = await getUser({ token, uid: id });
	const loggedUser = await getUser({ token, uid: uid });

	return userStore(user, true, loggedUser);
};

const settingsSubmit = async (state: PropertyType, token: string) => {
	const success = await updateUser({ token, ...state });

	return { errors: [], success };
};

const resetEmailSubmit = async (state: PropertyType, token: string) => {
	const { userId } = state;
	const success = await resetEmail({ token, userId, url: baseUrl });

	return { errors: [], success };
};

const getUpdatedUser = async (state: PropertyType, payload: PropertyType) => {
	const user = await getUser({ uid: state.userId, token: payload.token });
	payload.setUser(user);
};

const onImageChange = async (state: PropertyType, payload: PropertyType) => {
	const success = await updateUserImage({ ...state, ...payload });

	return { errors: [], success };
};

const onAccountDelete = async (state: PropertyType, token: string) => {
	const success = await deleteAccount({
		token,
		userIds: [state.userId],
	});

	return { errors: [], success };
};

const onAccountActivate = async (state: PropertyType, token: string) => {
	const success = await activateAccount({
		token,
		userIds: [state.userId],
	});

	return { errors: [], success };
};

const onAccountDeactivate = async (state: PropertyType, token: string) => {
	const success = await deactivateAccount({
		token,
		userIds: [state.userId],
	});

	return { errors: [], success };
};

export const actions: ActionsType = {
	USER_ID: (state, payload) => ({ userId: payload }),
	USERNAME: (state, payload) => ({ username: payload }),
	USERNAME_INVALID: (state, payload) => ({ usernameInvalid: payload }),
	EMAIL: (state, payload) => ({ email: payload }),
	EMAIL_INVALID: (state, payload) => ({ emailInvalid: payload }),
	IMAGE_UPDATE: (state, payload) => onRequest(() => onImageChange(state, payload)),
	ERROR: (state, payload) => ({ errors: payload }),
	SUCCESS: (state, payload) => ({ success: payload }),
	USER_INIT: (state, payload) => onRequest(() => userInit(payload)),
	PARTNERS_UPDATE: (state, payload) => ({ activePartners: payload }),
	GET_UPDATED_USER: (state, payload) => onRequest(() => getUpdatedUser(state, payload)),
	SETTINGS_SUBMIT: (state, payload) => onRequest(() => settingsSubmit(state, payload)),
	RESET_EMAIL_SUBMIT: (state, payload) => onRequest(() => resetEmailSubmit(state, payload)),
	DELETE_ACCOUNT: (state, payload) => onRequest(() => onAccountDelete(state, payload)),
	ACTIVATE_ACCOUNT: (state, payload) => onRequest(() => onAccountActivate(state, payload)),
	DEACTIVATE_ACCOUNT: (state, payload) => onRequest(() => onAccountDeactivate(state, payload)),
	MESSAGE_INVALIDATE: () => ({ errors: [], success: '' }),
};
